import type { ReactNode } from "react";
import { toast } from "@carbon6/c6-frontend-design";
import { truncationMarkerProps } from "./truncationCell";
import styles from "./branchHash.module.css";
import monoStyles from "./dataTableMono.module.css";

const EMPTY_CELL = "-";

// 표와 상세는 같은 자릿수로 커밋을 줄인다. 이 자릿수는 Results 표가 BE 에게 받는 git_short_hash 와 같다.
export const SHORT_SHA_LENGTH = 8;

// training-infra 의 _LEARNING_CORE_SHA_RE 와 같은 판정이다. 커밋을 고정한 제출은
// `learning_core.ref` 자체가 40-hex 다. 그래서 "브랜치" 자리에 sha 가 온다.
const FULL_SHA_RE = /^[0-9a-f]{40}$/i;

/**
 * "브랜치" 자리에 온 값이 고정한 커밋(full sha)인지 판정한다.
 *
 * Args:
 *   branch: 브랜치 자리 값.
 *
 * Returns:
 *   40-hex 면 true.
 */
export function isFullSha(branch: string | null | undefined): boolean {
  return branch ? FULL_SHA_RE.test(branch) : false;
}

/**
 * full SHA 를 표시용 길이로 줄인다. 값이 없으면 null 을 반환한다.
 *
 * Args:
 *   sha: full 커밋 SHA(또는 부재).
 *
 * Returns:
 *   앞 8자, 입력이 비어 있으면 null.
 */
export function shortSha(sha: string | null | undefined): string | null {
  return sha ? sha.slice(0, SHORT_SHA_LENGTH) : null;
}

export type BranchHashParts = {
  branch: string | null | undefined;
  hash: string | null | undefined;
  fullHash?: string | null;
};

export function formatBranchHash(
  branch: string | null | undefined,
  hash: string | null | undefined,
): string {
  return [branch, hash].filter(Boolean).join(" @ ") || EMPTY_CELL;
}

async function copyPart(value: string, label: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label}를 복사했습니다.`);
  } catch {
    toast.error("복사하지 못했습니다. 다시 시도해 주세요.");
  }
}

// 표와 인라인 요약이 같은 문자열·복사 규칙을 쓴다. 각 버튼이 자기 잘림 마커를 소유한다.
// 그래서 긴 브랜치가 잘려도 사용자는 hash 를 따로 클릭하고 전체 값을 확인할 수 있다.
export function BranchHashCell({ branch, hash, fullHash }: BranchHashParts): ReactNode {
  const parts = [
    { label: "Branch", value: branch, copyValue: branch },
    { label: "Commit hash", value: hash, copyValue: fullHash || hash },
  ];
  return (
    <span className={`${styles.value} ${monoStyles.mono}`}>
      {!branch && !hash ? formatBranchHash(branch, hash) : null}
      {parts.map(({ label, value, copyValue }) =>
        value ? (
          <span key={label} className={styles.part}>
            {label === "Commit hash" && branch ? (
              <span className={styles.separator}>{" @ "}</span>
            ) : null}
            <button
              type="button"
              className={styles.link}
              aria-label={`${label} 복사: ${value}`}
              {...truncationMarkerProps}
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                void copyPart(copyValue || value, label);
              }}
            >
              {value}
            </button>
          </span>
        ) : null,
      )}
    </span>
  );
}
